import React, { Fragment, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ComboboxInput, Combobox as HeadlessCombobox, ComboboxOptions } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';

const propTypes = {
  name: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onChangeInput: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  isError: PropTypes.bool,
  children: PropTypes.node,
  hasItems: PropTypes.bool,
  noItemsMsg: PropTypes.string,
  scrollElementRef: PropTypes.object,
  virtualItems: PropTypes.object,
  onScroll: PropTypes.func,
  isLoadingMore: PropTypes.bool,
};

const defaultProps = {
  placeholder: 'Search fonts...',
  noItemsMsg: 'No fonts found',
};

const FontComboboxCustom = props => {
  const {
    name,
    value,
    onChange,
    onChangeInput,
    onClear,
    placeholder,
    isError,
    children,
    hasItems,
    noItemsMsg,
    scrollElementRef,
    virtualItems,
    onScroll,
    isLoadingMore,
    defaultLabel,
  } = props;

  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [query, setQuery] = useState('');

  const handleInputChange = event => {
    setQuery(event.target.value);
    onChangeInput(event);
  };

  const handleClear = e => {
    e.preventDefault();
    e.stopPropagation();
    setQuery('');
    onClear();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Fragment>
      <HeadlessCombobox
        value={value?._id || null}
        onChange={selectedId => {
          onChange(selectedId ?? '');
          setTimeout(() => {
            if (selectedId !== null) {
              inputRef.current?.blur();
              setQuery('');
            }
          }, 0);
        }}
        immediate
      >
        <div className="relative">
          <ComboboxInput
            ref={inputRef}
            className={`h-8 w-full min-w-0 rounded-lg border border-input bg-transparent dark:bg-input/30 py-1 pl-2.5 pr-9 text-sm transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ${isError ? 'border-destructive ring-3 ring-destructive/20' : ''}`}
            displayValue={val => {
              if (isFocused) return query;
              if (!val) return defaultLabel || 'Auto';
              return value?.name || val;
            }}
            onChange={handleInputChange}
            placeholder="Search fonts..."
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            onFocus={() => {
              setIsFocused(true);
              setQuery('');
            }}
            onBlur={() => {
              setIsFocused(false);
              setQuery('');
            }}
          />

          {/* Icons container */}
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
          </div>

          {/* Dropdown options with virtualization */}
          <ComboboxOptions
            ref={scrollElementRef}
            className="absolute z-50 mt-2 max-h-60 w-full overflow-auto rounded-lg bg-popover text-popover-foreground shadow-md ring-1 ring-foreground/10"
            onScroll={onScroll}
          >
            {hasItems ? (
              <div
                style={{
                  height: virtualItems?.totalHeight || 0,
                  position: 'relative',
                }}
              >
                {children}
              </div>
            ) : (
              <div className="px-4 py-2 text-muted-foreground text-sm">{noItemsMsg}</div>
            )}

            {isLoadingMore && (
              <div className="flex items-center justify-center text-sm text-muted-foreground px-3 py-2 h-12">
                Loading more fonts...
              </div>
            )}
          </ComboboxOptions>
        </div>
      </HeadlessCombobox>
    </Fragment>
  );
};

FontComboboxCustom.propTypes = propTypes;
FontComboboxCustom.defaultProps = defaultProps;

export default FontComboboxCustom;
